import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Shield, Users, Briefcase, Activity, Lock } from "lucide-react";
import { API } from "@/App";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("adminUser", JSON.stringify(response.data));
      toast.success("Welcome Admin!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  const statTiles = [
    { label: "Platform Active", value: "24/7", icon: Activity, accent: "from-[#FF0080] to-[#FF4DA6]" },
    { label: "Secure Auth", value: "JWT", icon: Lock, accent: "from-[#B026FF] to-[#7B2FBE]" },
    { label: "Creators", value: "10K+", icon: Users, accent: "from-[#FF0080] to-[#B026FF]" },
    { label: "Brands", value: "500+", icon: Briefcase, accent: "from-[#B026FF] to-[#FF0080]" },
  ];

  return (
    <div className="min-h-screen bg-[#08070D] text-white font-manrope relative overflow-hidden">
      {/* Ambient glow orbs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF0080]/25 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#B026FF]/25 blur-[160px]" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-[#FF0080]/10 blur-[120px]" />

      {/* Grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,0,128,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(176,38,255,0.35) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* LEFT — Brand / Context panel */}
        <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 border-r border-white/5">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF0080] to-[#B026FF] flex items-center justify-center shadow-[0_0_40px_rgba(255,0,128,0.5)]">
              <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-manrope font-extrabold text-2xl tracking-tight leading-none">
                <span className="bg-gradient-to-r from-[#FF0080] via-[#FF4DA6] to-[#B026FF] bg-clip-text text-transparent">
                  Found
                </span>
                <span className="text-white">Star</span>
              </p>
              <p className="font-mono text-[10px] text-white/40 tracking-[0.3em] uppercase mt-1">
                Admin · Control · Panel
              </p>
            </div>
          </div>

          {/* Hero text */}
          <div className="space-y-6 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl">
              <span className="w-2 h-2 rounded-full bg-[#FF0080] animate-pulse shadow-[0_0_10px_#FF0080]" />
              <span className="font-mono text-[11px] tracking-widest uppercase text-white/70">
                System Online
              </span>
            </div>
            <h1 className="font-manrope font-extrabold text-5xl xl:text-6xl leading-[1.05] tracking-tight">
              Command center for{" "}
              <span className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] bg-clip-text text-transparent">
                the entire stage.
              </span>
            </h1>
            <p className="text-white/60 text-lg leading-relaxed">
              Verify creators, approve brands, resolve deals, manage payouts,
              and curate the landing page — all from one unified panel.
            </p>
          </div>

          {/* Glass-morphism stat tiles */}
          <div className="grid grid-cols-2 gap-4">
            {statTiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <div
                  key={tile.label}
                  className="group relative overflow-hidden rounded-2xl bg-white/[0.04] backdrop-blur-2xl border border-white/10 p-4 hover:border-white/20 transition-all hover:-translate-y-0.5"
                >
                  <div
                    className={`absolute -top-10 -right-10 w-24 h-24 rounded-full bg-gradient-to-br ${tile.accent} opacity-30 blur-2xl group-hover:opacity-50 transition-opacity`}
                  />
                  <div className="relative flex items-center justify-between">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {tile.label}
                      </p>
                      <p className="font-manrope font-bold text-2xl mt-1 text-white">
                        {tile.value}
                      </p>
                    </div>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tile.accent} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Login form */}
        <div className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            {/* Mobile logo (shown below lg) */}
            <div className="lg:hidden flex items-center gap-3 mb-10">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF0080] to-[#B026FF] flex items-center justify-center shadow-[0_0_30px_rgba(255,0,128,0.5)]">
                <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <p className="font-manrope font-extrabold text-xl">
                <span className="bg-gradient-to-r from-[#FF0080] to-[#B026FF] bg-clip-text text-transparent">
                  Found
                </span>
                <span className="text-white">Star</span>
              </p>
            </div>

            {/* Glass card */}
            <div className="relative rounded-3xl bg-white/[0.03] backdrop-blur-2xl border border-white/10 p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(255,0,128,0.25)]">
              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#FF0080]/70 to-transparent" />

              <div className="mb-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF4DA6] mb-3">
                  · Administrator Access ·
                </p>
                <h2 className="font-manrope font-extrabold text-3xl tracking-tight">
                  Sign in to continue
                </h2>
                <p className="text-white/50 text-sm mt-2">
                  Authorized personnel only. All sessions are logged.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
                    Admin Email
                  </label>
                  <input
                    type="email"
                    placeholder="admin@foundstar.in"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 outline-none focus:border-[#FF0080] focus:ring-2 focus:ring-[#FF0080]/20 transition font-manrope"
                    data-testid="admin-email-input"
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 pr-12 text-white placeholder:text-white/30 outline-none focus:border-[#B026FF] focus:ring-2 focus:ring-[#B026FF]/20 transition font-mono tracking-wider"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#FF0080] transition"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#FF0080] to-[#B026FF] py-4 text-white font-manrope font-semibold shadow-[0_10px_40px_-10px_rgba(255,0,128,0.6)] hover:shadow-[0_15px_60px_-10px_rgba(176,38,255,0.7)] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="admin-login-btn"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-[#B026FF] to-[#FF0080] opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Authenticating...
                      </>
                    ) : (
                      <>
                        Access Admin Panel
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </span>
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between text-xs">
                <a
                  href="/"
                  className="font-mono uppercase tracking-widest text-white/40 hover:text-[#FF0080] transition"
                >
                  ← Main Site
                </a>
                <span className="font-mono text-white/30">v2.0 · {new Date().getFullYear()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
